import { useEffect, useState } from "react";
import { Button, Input } from "reactstrap";
import { useAtomValue } from "jotai";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { Page } from "../../../types/GlobalInterfaces";
import { View } from "../../../types/PageInterfaces";
import BasicTableView from "../../../views/supportal/CloudListView/BasicTableView";
import { BasicSearch } from "../../../views/cloud/CloudSettingsHome";
import AppPageHeader from "../../Headers/AppPageHeader";
import TenantAPI from "../../../api/api/TenantAPI";
import PageAPI from "../../../api/api/PageAPI";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import { newToastEvent } from "../../../utils/ToastUtil";
import IconButton from "../../../elements/IconButton";
import { AppConfirm } from "../../../utils/PopupUtil";
import useRecord from "../../../api/api-hooks/records/useRecord";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import useWorkspacePermissions from "../../../hooks/useWorkspacePermissions";
import usePagePermissions from "../../../hooks/usePagePermissions";
import FlowChartDrawer from "./FlowChartDrawer";
import { FlowChart } from "./FlowChartDrawer";

interface Props {
  page: Page;
  view: View;
  viewLoading: boolean;
}

export interface FlowChartRecord extends FlowChart {}

export default function PageViewFlowChartList({
  page,
  view,
  viewLoading,
}: Props) {
  const { cPermissions } = useCloudPermissions();
  const { wPermissions } = useWorkspacePermissions();
  const { pPermissions } = usePagePermissions();
  const [search, setSearch] = useState("");
  const [params, setParams] = useSearchParams();
  const [addFlowDrawer, setAddFlowDrawer] = useState(false);
  const [editFlowID, setEditFlowID] = useState("");
  const { deleteFlow, deleting } = useDeleteFlow({ view, pageID: page.id });
  const { record, recordLoading } = useRecord({
    moduleAPIName: "Flows",
    recordID: editFlowID,
  });

  let hasPermissions =
    cPermissions.cloud_modify_page ||
    wPermissions.workspace_modify_page ||
    pPermissions.page_modify_page;

  let flows = view?.Flows;

  if (search) {
    flows = flows?.filter(
      (f) => f.Name.includes(search) || f.Description.includes(search)
    );
  }

  return (
    <>
      <div className="mt--3">
        <AppPageHeader
          title={"Flows"}
          description={`The flows created inside ${view?.Name}`}
          customContent={
            <div className="d-flex justify-content-between align-items-center mt-2 mt-md-0">
              <div style={{ width: 300 }}>
                <BasicSearch
                  setSearchValue={setSearch}
                  placeholder={`Search Flows`}
                />
              </div>
              {hasPermissions && (
                <Button
                  className="ml-3"
                  color="primary"
                  onClick={() => setAddFlowDrawer(true)}
                >
                  Add
                </Button>
              )}
            </div>
          }
        />
        <BasicTableView
          loading={viewLoading}
          items={flows || []}
          title={undefined}
          getItemTitle={(f) => f.Name}
          getItemSubtitle={(f) => f.Description}
          onClick={(f) => selectFlow(f.RecordID)}
          CustomRenderer={
            hasPermissions
              ? (f) => (
                  <>
                    <IconButton
                      icon="cog"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setAddFlowDrawer(true);
                        setEditFlowID(f.RecordID);
                      }}
                    />
                    <IconButton
                      icon="close"
                      color="danger"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        deleteFlow(f.RecordID);
                      }}
                      loading={deleting === f.RecordID}
                    />
                  </>
                )
              : undefined
          }
        />
      </div>
      <FlowChartDrawer
        open={addFlowDrawer}
        onClose={() => {
          setAddFlowDrawer(false);
          setEditFlowID("");
        }}
        flowChartID={editFlowID}
        flowChart={record}
        view={view}
        page={page}
        recordLoading={recordLoading}
      />
    </>
  );

  function selectFlow(flowID: string) {
    params.set("flow", flowID);
    setParams(params);
  }
}

function useDeleteFlow({ view, pageID }: { view: View; pageID: string }) {
  const [deleting, setDeleting] = useState("");
  const workspaceID = useAtomValue(workspaceIdAtom);
  const client = useQueryClient();

  async function deleteFlow(recordID: string) {
    const result = await AppConfirm({
      title: "Delete Flow?",
      text: "Are you sure you want to delete this Flow? This action cannot be undone.",
    });

    if (!result.isConfirmed) return;

    try {
      if (!view?.Flows) throw new Error("No flows found");

      setDeleting(recordID);

      await TenantAPI.deleteRecord("Flows", recordID);

      const viewFlows = view.Flows.filter((f) => f.RecordID !== recordID);

      const viewData = await PageAPI.updateView(
        workspaceID,
        pageID,
        view?._id,
        {
          Flows: viewFlows,
        }
      );
      const updatedView = viewData.data.data.view;

      updatedView &&
        client.setQueryData(["view", updatedView?._id], updatedView);

      newToastEvent("success", "Successfully deleted flow.");
    } catch {
    } finally {
      setDeleting("");
    }
  }

  return { deleteFlow, deleting };
}
